import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { normalizeEmail, toDate } from '../lib/format';
import { AllowedEmail } from '../types';

const ALLOWED = 'allowedEmails';

export interface MigrationSummary {
  invited: number;
  membersMigrated: number;
  shareLinksMigrated: number;
}

/**
 * Invitation list. Only addresses stored here (lower-case document id) can use
 * the app; the Firestore rules enforce this on every request.
 */
export const accessService = {
  async isInvited(email: string): Promise<boolean> {
    const snap = await getDoc(doc(db, ALLOWED, normalizeEmail(email)));
    return snap.exists();
  },

  /**
   * Adds an address to the invitation list (idempotent).
   * Project owners must pass the projectId they are inviting to.
   */
  async invite(email: string, invitedBy: string, opts: { projectId?: string; note?: string } = {}): Promise<void> {
    const normalized = normalizeEmail(email);
    await setDoc(
      doc(db, ALLOWED, normalized),
      {
        email: normalized,
        invitedBy,
        invitedAt: serverTimestamp(),
        ...(opts.projectId ? { projectId: opts.projectId } : {}),
        ...(opts.note ? { note: opts.note } : {}),
      },
      { merge: true }
    );
  },

  async revoke(email: string): Promise<void> {
    await deleteDoc(doc(db, ALLOWED, normalizeEmail(email)));
  },

  async list(): Promise<AllowedEmail[]> {
    const snapshot = await getDocs(collection(db, ALLOWED));
    return snapshot.docs
      .map((d) => {
        const data = d.data();
        return {
          email: d.id,
          invitedBy: data.invitedBy,
          invitedAt: toDate(data.invitedAt),
          projectId: data.projectId,
          note: data.note,
        } as AllowedEmail;
      })
      .sort((a, b) => a.email.localeCompare(b.email));
  },

  /**
   * One-off admin migration after the security update:
   *  - invites every project member and every project owner,
   *  - moves member documents to deterministic ids,
   *  - moves share links to hash-keyed documents and publishes project cards.
   */
  async migrateEverything(adminUid: string): Promise<MigrationSummary> {
    // Imported lazily to avoid a circular import at module load.
    const { projectService } = await import('./projectService');
    const { shareService } = await import('./shareService');

    const summary: MigrationSummary = { invited: 0, membersMigrated: 0, shareLinksMigrated: 0 };
    const emails = new Set<string>();

    const [projectsSnap, usersSnap, membersSnap] = await Promise.all([
      getDocs(collection(db, 'projects')),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'projectMembers')),
    ]);

    const userEmailByUid = new Map<string, string>();
    usersSnap.docs.forEach((d) => {
      const email = d.data().email;
      if (email) userEmailByUid.set(d.id, normalizeEmail(email));
    });

    // Owners of existing projects keep access.
    projectsSnap.docs.forEach((d) => {
      const ownerEmail = userEmailByUid.get(d.data().createdBy);
      if (ownerEmail) emails.add(ownerEmail);
    });

    // Admins keep access even if their admin flag is later revoked.
    usersSnap.docs.forEach((d) => {
      if (d.data().isAdmin === true && d.data().email) emails.add(normalizeEmail(d.data().email));
    });

    for (const email of Array.from(emails)) {
      await this.invite(email, adminUid, { note: 'migration' });
    }

    const projectIdsWithMembers = new Set(membersSnap.docs.map((d) => d.data().projectId as string));
    for (const projectId of Array.from(projectIdsWithMembers)) {
      summary.membersMigrated += await projectService.migrateLegacyMembers(projectId, adminUid);
    }
    membersSnap.docs.forEach((d) => d.data().email && emails.add(normalizeEmail(d.data().email)));
    summary.invited = emails.size;

    for (const projectDoc of projectsSnap.docs) {
      summary.shareLinksMigrated += await shareService.migrateLegacyLinks(projectDoc.id);
    }

    return summary;
  },
};
